/**
 * Typed surface registry for CheckList feedback (contract §6.1, F-10).
 *
 * surfaceId values are registry keys — never derived from window.location.
 * Each entry's likelyFiles / promptFraming feed the hub's improvement-prompt builder.
 *
 * Seed set: CUSTOMER_FEEDBACK_PLAN §4.2. Only ai.generate.results and
 * community.list-detail mount thumbs (F-11); the rest exist so form/report
 * submissions carry a stable, typed id.
 */

export interface SurfaceDef {
  label: string;
  /** 1-3 sentences: what this surface does, what "wrong" means here, what to suspect first. */
  promptFraming: string;
  /** Files an investigator should open first. Keep to 2-5, most-likely first. */
  likelyFiles: string[];
}

export function defineSurfaces<T extends Record<string, SurfaceDef>>(surfaces: T): T {
  return surfaces;
}

export type SurfaceIdOf<T extends Record<string, SurfaceDef>> = keyof T & string;

export const SURFACES = defineSurfaces({
  "ai.generate.results": {
    label: "AI generate — results",
    promptFraming:
      "A user rated or disputed a Bedrock-generated list. Suspect prompt framing, " +
      "kind/packing vs todo branching, cache hits returning stale content, or empty " +
      "results — never reconstruct the generated item text from the payload.",
    likelyFiles: [
      "web/src/components/CreateWithAiModal.tsx",
      "api/src/handlers/ai.ts",
      "web/src/lib/feedback/surfaces.ts",
    ],
  },
  "community.list-detail": {
    label: "Community list detail",
    promptFraming:
      "A user rated a published community list they were deciding whether to copy. " +
      "Suspect copy/instantiate gaps, stale metadata, or inappropriate content reports " +
      "(category other + listId in allowlisted payload) — never include list names or item text.",
    likelyFiles: [
      "web/src/pages/CommunityPage.tsx",
      "api/src/handlers/community.ts",
      "web/src/lib/feedback/surfaces.ts",
    ],
  },
  "form.contact": {
    label: "Contact form",
    promptFraming:
      "A user sent a free-form contact message. Treat it as a conversation, not a bug. " +
      "Look for actionable asks (feature ideas, account, privacy) vs. general hellos.",
    likelyFiles: [
      "web/src/pages/ContactPage.tsx",
      "web/src/components/feedback/FeedbackForm.tsx",
      "web/src/lib/feedback/client.ts",
    ],
  },
  "form.bug-report": {
    label: "Bug report dialog",
    promptFraming:
      "A user filed a bug from the site-wide report dialog. Reproduce from the route and " +
      "browser/OS fields in context. Prefer fixing the reported surface over the dialog itself.",
    likelyFiles: [
      "web/src/components/feedback/ReportProblemDialog.tsx",
      "web/src/components/feedback/FeedbackForm.tsx",
      "web/src/lib/feedback/client.ts",
    ],
  },
  "form.feature-request": {
    label: "Feature request dialog",
    promptFraming:
      "A user requested a feature or improvement. Evaluate scope against existing Lists / " +
      "Templates / Checklists / sharing; prefer small enhancements over new infra.",
    likelyFiles: [
      "web/src/components/feedback/ReportProblemDialog.tsx",
      "web/src/lib/feedback/surfaces.ts",
    ],
  },
  "page.404": {
    label: "404 page",
    promptFraming:
      "A user hit a missing route and chose to report it. Check for broken internal links, " +
      "stale share URLs, and SPA catch-all regressions — do not treat /s/:token misses as 404s.",
    likelyFiles: ["web/src/pages/NotFoundPage.tsx", "web/src/App.tsx"],
  },
  "app.error-boundary": {
    label: "Top-level error boundary",
    promptFraming:
      "A render error was caught and the user chose to report it. Start from errorName / " +
      "errorMessage / truncated stack in the payload — never include user content.",
    likelyFiles: ["web/src/components/ErrorBoundary.tsx", "web/src/App.tsx"],
  },
  "template.instantiate": {
    label: "Template instantiate",
    promptFraming:
      "A user hit trouble instantiating a template into a checklist. Suspect snapshot logic " +
      "silently skipping deleted lists (PLAN.md), section ordering, or membership checks.",
    likelyFiles: [
      "api/src/handlers/checklists.ts",
      "web/src/pages/TemplateEditorPage.tsx",
      "shared/src/keys.ts",
    ],
  },
  "share.access-request": {
    label: "Share access request",
    promptFraming:
      "A user hit trouble on the public access-request flow. Suspect rate limits, owner " +
      "notification delivery, or token/redaction issues — never log share tokens.",
    likelyFiles: [
      "web/src/pages/RequestAccessPage.tsx",
      "api/src/handlers/sharing.ts",
      "web/src/lib/feedback/context.ts",
    ],
  },
  "export.pdf": {
    label: "PDF export",
    promptFraming:
      "A user reported a PDF export problem. Suspect layout/column choice, font loading, " +
      "or empty sections — payload may include counts only, never checklist item text.",
    likelyFiles: [
      "web/src/lib/pdf/exportPdf.ts",
      "web/src/lib/pdf/layout.ts",
      "web/src/pages/ChecklistPage.tsx",
    ],
  },
  "assistant.member.degraded": {
    label: "AI assistant — degraded",
    promptFraming:
      "User reached the assistant while it could not call the model; suspect budget/ceiling " +
      "settings or Bedrock availability.",
    likelyFiles: [
      "api/src/aiChat.ts",
      "api/src/lib/aiChat/ceiling.ts",
      "web/src/components/ai/AiChatPanel.tsx",
    ],
  },
  "assistant.member.reply": {
    label: "AI assistant — reply feedback",
    promptFraming:
      "User marked an assistant reply unhelpful; suspect prompt grounding, a missing KB " +
      "section, or a wrong tool result.",
    likelyFiles: [
      "api/src/lib/aiChat/prompt.ts",
      "api/src/lib/aiChat/readTools.ts",
      "web/src/components/ai/AiChatPanel.tsx",
    ],
  },
  "assistant.member.ticket": {
    label: "AI assistant — drafted ticket",
    promptFraming:
      "User reviewed and submitted a ticket the assistant drafted after searching the help " +
      "notes. Treat the body as a real report; suspect a KB gap if the draft itself points " +
      "at missing or wrong help content.",
    likelyFiles: [
      "api/src/lib/aiChat/kb.ts",
      "api/src/lib/aiChat/prompt.ts",
      "web/src/components/ai/AiChatPanel.tsx",
    ],
  },
});

export type SurfaceId = SurfaceIdOf<typeof SURFACES>;

/** Categories for optional thumbs-down / bug reason chips (contract §4 shape). */
export const INCIDENT_CATEGORIES = [
  { id: "wrong_result", label: "Gave the wrong answer" },
  { id: "broken", label: "Something didn't work" },
  { id: "confusing", label: "Confusing or hard to use" },
  { id: "missing", label: "Missing something I needed" },
  { id: "slow", label: "Too slow" },
  { id: "other", label: "Something else" },
] as const;

export type IncidentCategoryId = (typeof INCIDENT_CATEGORIES)[number]["id"];
